/* eslint-disable react/jsx-props-no-spreading */
/**
 * @fileoverview Componente Molecule para la gestión y visualización de tópicos de conversación asociados a un cliente en el módulo Inbox.
 * @module ConversationTopicsMC
 * @category Component - Molecule
 * @since 2.7.0
 * @version 3.1.0 - Optimización de performance, separación estricta de capas y documentación JSDoc completa.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import moment from 'moment';
import ButtonComponent from '../../../components/ButtonMC';
import ConversationTopicsModalMC from './ConversationTopicsModalMC';
import LoadingMC from '../../../components/LoadingMC';
import useConversationTopicsData from '../../../hooks/inbox/data/useConversationTopicsData';
import useConversationTopicsUI from '../../../hooks/inbox/ui/useConversationTopicsUI';
import { useTraceUpdate } from '../../../hooks/shared/useDebugRenders';
import IconMC from '../../../components/IconMC';

/**
 * ConversationTopicsMC - Componente para la gestión y visualización de tópicos de conversación asociados a un cliente en el módulo Inbox.
 */
const ConversationTopicsMC = React.memo(({ conversation }) => {
  const { t } = useTranslation('InboxMC');

  // Data Layer: Manejo de datos y estado de topics
  const {
    loading,
    customerTopics,
    viewMore,
    showTopicSelector,
    handleOpenTopicSelector,
    handleCloseTopicSelector,
    handleDeleteTopic,
    handleViewMore
  } = useConversationTopicsData(conversation);

  // UI Layer: Configuración de botones y estilos
  const { toggleButtonConfig } = useConversationTopicsUI();

  // Debug tracking (solo desarrollo)
  useTraceUpdate('ConversationTopicsMC', {
    customerId: conversation?.customerId,
    platform: conversation?.platform,
    loading,
    customerTopicsLength: customerTopics?.length,
    showTopicSelector
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-4">
      <p className="tw-text-xs tw-font-bold tw-mb-2">
        {t('ConversationTopics:conversation_topics.messages.topic_label')}
      </p>
      <div className="tw-flex tw-justify-center">
        <ButtonComponent
          outlined
          text={t('customerDetails.conversation_topics.add')}
          onClick={handleOpenTopicSelector}
          className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-mb-2"
        />
      </div>
      <LoadingMC loading={loading} />
      {customerTopics?.map((topic) => (
        <div
          key={`conversation-${topic.id}`}
          className="tw-flex tw-justify-between tw-items-start tw-mb-2 tw-border tw-border-[#D6DCE1] tw-rounded-[12px] tw-py-2 tw-px-4"
        >
          <div className="tw-flex tw-flex-col">
            <span className="tw-text-xs tw-font-medium tw-text-gray-950">{topic.name}</span>
            <span className="tw-text-2xs tw-text-gray-600">{topic.date ? moment(topic.date).local().format('DD-MM-YYYY HH:mm') : ''}</span>
          </div>
          <div className="tw-flex tw-flex-row tw-justify-end tw-items-center tw-gap-1 tw-mb-2">
            <div className="actions-conv-topics-icon-tooltip" data-tooltip-content={t('customerDetails.deleteButtonTooltip')}>
              <IconMC name="trash-2-outline" size="base" onClick={() => handleDeleteTopic(topic.id)} />
            </div>
            <ReactTooltip
              place="top"
              anchorSelect=".actions-conv-topics-icon-tooltip"
              className="tw-z-50"
            />
          </div>
        </div>
      ))}
      {viewMore && (
        <div className="tw-flex tw-justify-center tw-mt-1">
          <ButtonComponent
            {...toggleButtonConfig}
            text={t('customerDetails.showMore')}
            icon="arrow-ios-downward-outline"
            onClick={handleViewMore}
          />
        </div>
      )}
      {showTopicSelector && (
        <ConversationTopicsModalMC
          customerId={conversation.customerId}
          platform={conversation.platform}
          chatId={conversation.id}
          toggleModal={handleCloseTopicSelector}
        />
      )}
    </div>
  );
});

ConversationTopicsMC.displayName = 'ConversationTopicsMC';

export default ConversationTopicsMC;
